// ─── 🔔 «Da segnare»: le partite con l'acconto finite senza incasso ─────────
//
// Voce 49, 27/09/2026, mockup «Campo vuoto, da segnare» approvato: *«1 sì, 2 A,
// 3 A»*. Prima l'acconto si chiudeva da solo alla fine della partita, e un campo
// vuoto non visto in quell'ora e mezza non si poteva più segnare. Adesso la
// partita aspetta qui, in cima al Calendario, finché la Segreteria non dice:
//   · ✅ «Si è giocata» ⇒ l'acconto si scala dal totale (sul fisso resta per le prossime);
//   · 🚫 «Campo vuoto» ⇒ il no-show di sempre: l'acconto resta al circolo (voce 50), e il suo messaggio.
// ⚖️ Il programma non decide niente da solo (scelta 2A), e le partite senza
// acconto non ci sono (scelta 3A): si segnano dalla scheda, come prima.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DaSegnare as Elenco, PartitaDaSegnare } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { giornoBreve } from '../../lib/time.js';
import { euro, usaModifica } from '../../routes/impostazioni/comune.js';

const CHIAVE = 'da-segnare';

/** Il numero sul pallino di «Calendario», nel menu. */
export function usaDaSegnare(abilitato: boolean): number {
  const q = useQuery({
    queryKey: [CHIAVE, 'conto'],
    queryFn: () => chiama<{ quante: number }>('GET', '/deposits/to-mark/count'),
    enabled: abilitato,
    refetchInterval: 120_000,
    staleTime: 60_000,
  });
  return q.data?.quante ?? 0;
}

const COME: Record<string, string> = {
  card: 'pagato online',
  credit: 'pagato coi Crediti',
  mixed: 'pagato coi Crediti e la carta',
  desk_cash: 'pagato al banco',
  desk_card: 'pagato al banco',
};

export function DaSegnare({ apri }: { apri: (p: PartitaDaSegnare) => void }): React.JSX.Element | null {
  const q = useQuery({
    queryKey: [CHIAVE, 'elenco'],
    queryFn: () => chiama<Elenco>('GET', '/deposits/to-mark'),
    refetchInterval: 120_000,
  });
  const partite = q.data?.partite ?? [];
  if (partite.length === 0) return null;
  return (
    <section className="da-segnare" aria-label="Partite da segnare">
      <h2>
        🔔 Da segnare:{' '}
        {partite.length === 1
          ? '1 partita finita senza incasso'
          : `${partite.length} partite finite senza incasso`}
      </h2>
      <p className="tenue piccolo">
        Non risulta pagato niente, a parte l’acconto: si sono giocate? Finché non scegli, l’acconto resta
        aperto.
      </p>
      {partite.map((p) => (
        <Riga key={p.bookingId} p={p} apri={apri} />
      ))}
    </section>
  );
}

function Riga({ p, apri }: { p: PartitaDaSegnare; apri: (p: PartitaDaSegnare) => void }): React.JSX.Element {
  const qc = useQueryClient();
  const modificabile = usaModifica('manage_payments');
  const [chiede, setChiede] = useState(false);
  const fisso = p.seriesId !== null;
  const rileggi = () => {
    void qc.invalidateQueries({ queryKey: [CHIAVE] });
    void qc.invalidateQueries({ queryKey: ['calendario'] });
    void qc.invalidateQueries({ queryKey: ['prenotazione', p.bookingId] });
  };
  const gesto = useMutation({
    meta: {
      fatto: (_d, g) =>
        g === 'giocata'
          ? fisso
            ? `segnata come giocata: l’acconto del campo fisso di ${p.organizerName} resta per le prossime partite`
            : `segnata come giocata: l’acconto di ${p.organizerName} si scala dal totale`
          : `campo vuoto segnato: l’acconto di ${euro(p.amountCents)} resta al circolo`,
    },
    mutationFn: (g: 'giocata' | 'vuoto') =>
      chiama('POST', `/bookings/${p.bookingId}/${g === 'giocata' ? 'played' : 'no-show'}`),
    onSuccess: rileggi,
    onError: rileggi,
  });
  return (
    <div className="partita-da-segnare">
      <div className="chi">
        <strong>
          {giornoBreve(p.date)} · {p.time} · {p.courtName}
        </strong>
        <span>
          {fisso ? 'campo fisso di ' : 'organizza '}
          <strong>{p.organizerName}</strong>
        </span>
      </div>
      <p className="tenue piccolo">
        💳 {fisso ? 'acconto del campo fisso' : 'acconto'} {euro(p.amountCents)}
        {p.paidWith && COME[p.paidWith] ? ` ${COME[p.paidWith]}` : ''} · {p.players} in campo · incassato{' '}
        {euro(0)}
      </p>
      {modificabile && !chiede && (
        <div className="bottoni">
          <button type="button" disabled={gesto.isPending} onClick={() => gesto.mutate('giocata')}>
            ✅ Si è giocata
          </button>
          <button
            type="button"
            className="bordato"
            disabled={gesto.isPending}
            onClick={() => setChiede(true)}
          >
            🚫 Campo vuoto
          </button>
          <button type="button" className="piatto" onClick={() => apri(p)}>
            Apri la partita
          </button>
        </div>
      )}
      {!modificabile && (
        <div className="bottoni">
          <button type="button" className="piatto" onClick={() => apri(p)}>
            Apri la partita
          </button>
        </div>
      )}
      {chiede && (
        <div className="conferma-campo-vuoto">
          <p>
            <strong>Segno il campo vuoto?</strong> L’acconto di {euro(p.amountCents)} resta al circolo, e la
            partita conta fra le saltate di {p.organizerName}. Nel bot riceve un messaggio cortese, con le
            strade per la prossima volta{fisso ? ' e il link per l’acconto nuovo del campo fisso' : ''}. Se
            vuoi restituirlo lo stesso, dopo c’è «💶 Accredita a discrezione» nella scheda.
          </p>
          <div className="bottoni">
            <button
              type="button"
              className="pericolo"
              disabled={gesto.isPending}
              onClick={() => gesto.mutate('vuoto', { onSettled: () => setChiede(false) })}
            >
              Sì, il campo era vuoto
            </button>
            <button type="button" className="piatto" onClick={() => setChiede(false)}>
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
